import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  GetUsers,
  SearchUser,
  BlocknUnblockUser,
  GetInoiceCount,
  GetStocksCount,
} from "apis/MyDukanBlocknUnblockApi";
import { toast } from "react-toastify";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import TableHeader from "components/Table/TableHeader";
import TableElement from "components/Table/TableElement";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import CircularIndeterminateModal from "utils/Loading";
import { useDispatch, useSelector } from "react-redux";
import { setLoadingPanel } from "../../redux/features/loading";
import { CircularProgress, IconButton, InputBase, Paper } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { formatDate } from "utils";
import { ActionButton, SearchInput, Toolbar as UIToolbar, TableCard, FormCard } from "utils/ui";

export default function MyDukanBlocknUnblock() {
  const [users, setUsers] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const isLoading = useSelector((state) => state.loadingReducer.isLoading);
  const dispatch = useDispatch();
  const debounceTimeout = useRef(null);

  const fetchUsers = useCallback(async () => {
    try {
      dispatch(setLoadingPanel(true));
      const response = await GetUsers({
        blocked: selectedFilter !== undefined ? selectedFilter : null,
        page: currentPage - 1,
        size: 10,
      });
      setUsers(response.data.users);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      dispatch(setLoadingPanel(false));
    }
  }, [selectedFilter, currentPage]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      fetchUsers();
    }
  }, [currentPage, selectedFilter, searchTerm]);

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      if (searchTerm.trim() !== "") {
        // Reset to page 1 when searching
        setCurrentPage(1);
      }
    }, 300);

    return () => clearTimeout(debounceTimeout.current);
  }, [searchTerm]);

  const handleSearch = async () => {
    try {
      dispatch(setLoadingPanel(true));
      const response = await SearchUser({ keyword: searchTerm });
      setUsers(response.data.users);
    } catch (error) {
      toast.error("Search failed");
    } finally {
      dispatch(setLoadingPanel(false));
    }
  };

  const handleFilterChange = (event) => {
    setSelectedFilter(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleBlockUnblock = async (userId) => {
    try {
      await BlocknUnblockUser(userId);
      fetchUsers();
    } catch (error) {
      toast.error("Action failed");
    }
  };

  const handleDialogOpen = (subscriptions) => {
    setSelectedSubscriptions(subscriptions);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedSubscriptions([]);
  };

  const handleViewInvoiceCount = async (userId) => {
    try {
      const count = await GetInoiceCount(userId);
      toast.success(`Invoice Count: ${count.data.invoices}`);
    } catch (error) {
      toast.error("Failed to fetch invoice count");
    }
  };

  const handleViewStockCount = async (userId) => {
    try {
      const count = await GetStocksCount(userId);
      toast.success(`Stock Count: ${count.data.stocks}`);
    } catch (error) {
      toast.error("Failed to fetch stock count");
    }
  };

  const getSubscriptions = (subs) => {
    if (!subs || subs.length === 0) {
      return false;
    }
    for (let i = 0; i < subs.length; i++) {
      if (subs[i].isSubUser === true) {
        return true;
      }
    }
    return false;
  };

  return (
    <>
      <CircularIndeterminateModal open={isLoading} />
      <FormCard>
        <UIToolbar>
          <FormControl sx={{ minWidth: 180, "& .MuiOutlinedInput-root": { height: 40 } }}>
            <InputLabel id="filter-select-label" sx={{ top: "-4px", '&.MuiInputLabel-shrink': { top: 0 } }}>Filter Users</InputLabel>
            <Select
              labelId="filter-select-label"
              id="filter-select"
              value={selectedFilter}
              label="Filter Users"
              onChange={handleFilterChange}
            >
              <MenuItem value={undefined}>All Users</MenuItem>
              <MenuItem value={true}>Blocked Users</MenuItem>
              <MenuItem value={false}>Unblocked Users</MenuItem>
            </Select>
          </FormControl>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
            sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 200 }}
          >
            <Paper
              component="div"
              sx={{
                p: "2px 4px",
                display: "flex",
                alignItems: "center",
                width: "100%",
                height: 40,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
              }}
            >
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder="Search User"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                inputProps={{ "aria-label": "search users" }}
              />
              <IconButton
                type="submit"
                sx={{ p: "6px" }}
                aria-label="search"
                disabled={isLoading}
              >
                <SearchIcon />
              </IconButton>
            </Paper>
          </Box>
        </UIToolbar>
      </FormCard>

      <TableCard>
        <div className="flex justify-center p-2 mb-2">
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={currentPage}
              color="primary"
              onChange={handlePageChange}
              size="medium"
            />
          </Stack>
        </div>

        <div className="overflow-x-auto py-3 px-4 lg:p-8">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <TableHeader heading="User Name" />
                <TableHeader heading="Phn no." />
                <TableHeader heading="E-mail" />
                <TableHeader heading="Created On" />
                <TableHeader heading="Last Login" />
                <TableHeader heading="Invoice Count" />
                <TableHeader heading="Stock Count" />
                <TableHeader heading="Is Subscribed" />
                <TableHeader heading="Subscription Details" />
                <TableHeader heading="Status" />
                <TableHeader heading="Action" />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.userId}
                  style={{ transition:"background 100ms" }} onMouseEnter={(e)=>e.currentTarget.style.background="#F8FAFC"} onMouseLeave={(e)=>e.currentTarget.style.background="transparent"}
                >
                  <TableElement value={user.userName || "N/A"} />
                  <TableElement value={user.userPhoneNumber || "N/A"} />
                  <TableElement value={user.userEmail || "N/A"} />
                  <TableElement value={formatDate(user.createdOn)} />
                  <TableElement value={formatDate(user.lastLogin)} />
                  <TableElement
                    value={
                      <Button
                        onClick={() => handleViewInvoiceCount(user.userId)}
                      >
                        View Invoice
                      </Button>
                    }
                  />
                  <TableElement
                    value={
                      <Button onClick={() => handleViewStockCount(user.userId)}>
                        View Stock
                      </Button>
                    }
                  />
                  <TableElement
                    value={getSubscriptions(user.subscription)}
                    type="boolean"
                  />
                  <TableElement
                    value={
                      <Button
                        onClick={() => handleDialogOpen(user.subscription)}
                      >
                        View Details
                      </Button>
                    }
                  />
                  <TableElement value={user.isLocked ? "Blocked" : "Active"} />
                  <TableElement
                    value={
                      <Button
                        onClick={() => handleBlockUnblock(user.userId)}
                        color={user.isLocked ? "primary" : "secondary"}
                      >
                        {user.isLocked ? "UnBlock" : "Block"}
                      </Button>
                    }
                  />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TableCard>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Subscription Details</DialogTitle>
        <DialogContent>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <TableHeader heading="Subscribed" />
                <TableHeader heading="Start Date" />
                <TableHeader heading="End Date" />
                <TableHeader heading="Last Update On" />
              </tr>
            </thead>
            <tbody>
              {selectedSubscriptions.map((sub, index) => (
                <tr
                  key={index}
                  style={{ transition:"background 100ms" }} onMouseEnter={(e)=>e.currentTarget.style.background="#F8FAFC"} onMouseLeave={(e)=>e.currentTarget.style.background="transparent"}
                >
                  <TableElement value={sub.isSubUser ? "true" : "false"} />
                  <TableElement value={formatDate(sub.startDate)} />
                  <TableElement value={formatDate(sub.endDate)} />
                  <TableElement value={formatDate(sub.lastUpdatedOn)} />
                </tr>
              ))}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>
    </>
  );
}
